import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_API_BASE ?? "";

function withAuth(opts = {}) {
  const token = localStorage.getItem("token") || "";
  return {
    credentials: "include",
    ...opts,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  };
}

async function j(url, opts) {
  const res = await fetch(url, withAuth(opts));
  const ct = res.headers.get("content-type") || "";
  const data = ct.includes("application/json") ? await res.json() : null;
  if (!res.ok || data?.ok === false) {
    throw new Error(data?.error || data?.message || `HTTP ${res.status}`);
  }
  return data;
}

const BTN = "px-3 py-2 rounded-xl bg-gradient-to-br from-[#6c56b5] to-[#2c204f] text-white border border-white/20 hover:opacity-95 disabled:opacity-60";
const BTN_LITE = "px-3 py-2 rounded-xl bg-white/10 border border-white/20 hover:bg-white/15 text-white disabled:opacity-60";
const BTN_DANGER = "px-3 py-2 rounded-xl bg-rose-600/80 hover:bg-rose-600 text-white disabled:opacity-60";
const INP = "w-full px-3 py-2 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-400/60";
const CARD = "bg-white/6 border border-white/10 rounded-2xl p-4";
const LABEL = "text-sm text-white/80";

function normalizeCinemas(raw) {
  const arr = Array.isArray(raw) ? raw : raw?.rows || raw?.data || [];
  return arr.map((c) => ({
    cinema_id: String(c.cinema_id ?? c.CINEMA_ID ?? c.id ?? ""),
    cinema_name: c.cinema_name ?? c.CINEMA_NAME ?? c.name ?? "Cinema",
  })).filter((c) => c.cinema_id);
}

function normalizeMovies(raw) {
  const arr = Array.isArray(raw) ? raw : raw?.rows || raw?.data || [];
  return arr.map((m) => ({
    movie_id: String(m.movie_id ?? m.MOVIE_ID ?? m.id ?? ""),
    movie_name: m.movie_name ?? m.MOVIE_NAME ?? m.title ?? m.name ?? "Movie",
  })).filter((m) => m.movie_id);
}

export default function AdminShowtimePricing() {
  const navigate = useNavigate();

  const [cinemas, setCinemas] = useState([]);
  const [movies, setMovies] = useState([]);
  const [rows, setRows] = useState([]);

  const [cinemaId, setCinemaId] = useState("");
  const [movieId, setMovieId] = useState("");
  const [movieSearch, setMovieSearch] = useState("");
  const [date, setDate] = useState("");

  const [editing, setEditing] = useState(null);
  const [basePrice, setBasePrice] = useState("");
  const [balconyExtra, setBalconyExtra] = useState("0");
  const [vipExtra, setVipExtra] = useState("0");

  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const filteredRows = useMemo(() => rows, [rows]);

  const filteredMovies = useMemo(() => {
    const q = movieSearch.trim().toLowerCase();
    const sorted = [...movies].sort((a, b) =>
      String(a.movie_name || "").localeCompare(String(b.movie_name || ""), "bg")
    );
    if (!q) return sorted;
    return sorted.filter((m) =>
      String(m.movie_name || "").toLowerCase().includes(q)
    );
  }, [movies, movieSearch]);

  async function loadBase() {
    const [c, m] = await Promise.all([
      j(`${API}/api/cinemas`),
      j(`${API}/api/movies`),
    ]);
    const cc = normalizeCinemas(c);
    setCinemas(cc);
    setMovies(normalizeMovies(m));
    if (!cinemaId && cc[0]) setCinemaId(cc[0].cinema_id);
  }

  async function loadRows() {
    if (!cinemaId) return;

    setLoading(true);
    setErr("");
    setMsg("");

    try {
      const qs = new URLSearchParams({ cinema_id: cinemaId });
      if (movieId) qs.set("movie_id", movieId);
      if (date) qs.set("show_date", date);

      const data = await j(`${API}/api/showtime-pricing/list?${qs.toString()}`);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setRows([]);
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadBase().catch((e) => setErr(e.message));
  }, []);

  useEffect(() => {
    loadRows();
  }, [cinemaId, movieId, date]);

  function editRow(r) {
    setEditing(r);
    setBasePrice(r.base_price === "" ? "" : String(r.base_price ?? ""));
    setBalconyExtra(r.balcony_extra === "" ? "0" : String(r.balcony_extra ?? 0));
    setVipExtra(r.vip_extra === "" ? "0" : String(r.vip_extra ?? 0));
    setMsg("");
    setErr("");
  }

  async function save() {
    if (!editing) return;

    setErr("");
    setMsg("");

    try {
      await j(`${API}/api/showtime-pricing/upsert`, {
        method: "POST",
        body: JSON.stringify({
          SHOWTIME_ID: editing.SHOWTIME_ID,
          base_price: Number(basePrice),
          balcony_extra: Number(balconyExtra || 0),
          vip_extra: Number(vipExtra || 0),
        }),
      });

      setMsg("✅ Цената за тази конкретна прожекция е запазена.");
      setEditing(null);
      await loadRows();
    } catch (e) {
      setErr(e.message);
    }
  }

  async function removeOverride(r = editing) {
    if (!r) return;

    setErr("");
    setMsg("");

    try {
      await j(`${API}/api/showtime-pricing/delete?SHOWTIME_ID=${encodeURIComponent(r.SHOWTIME_ID)}`, {
        method: "DELETE",
      });

      setMsg("🗑️ Override цената е изтрита.");
      setEditing(null);
      await loadRows();
    } catch (e) {
      setErr(e.message);
    }
  }

  return (
    <div className="min-h-screen p-6 text-white bg-gradient-to-br from-[#2c204f] via-[#161022] to-black">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
          <div>
            <h1 className="text-3xl font-extrabold">🎟️ Цени по прожекция</h1>
            <p className="text-white/70 text-sm mt-1">
              Най-висок приоритет: конкретен филм + дата + час.
            </p>
          </div>

          <button className={BTN_LITE} onClick={() => navigate("/admin/dashboard")}>
            ← Назад
          </button>
        </div>

        {err && <div className="mb-4 rounded-xl bg-rose-500/20 border border-rose-400/30 p-3">{err}</div>}
        {msg && <div className="mb-4 rounded-xl bg-emerald-500/20 border border-emerald-400/30 p-3">{msg}</div>}

        <div className={`${CARD} mb-5 grid grid-cols-1 md:grid-cols-4 gap-3`}>
          <label>
            <div className={LABEL}>Кино</div>
            <select className={INP} value={cinemaId} onChange={(e) => setCinemaId(e.target.value)}>
              {cinemas.map((c) => (
                <option key={c.cinema_id} value={c.cinema_id} className="text-black">
                  {c.cinema_name}
                </option>
              ))}
            </select>
          </label>

          <label>
            <div className={LABEL}>Филм</div>
            <input
              className={INP + " mb-2"}
              value={movieSearch}
              onChange={(e) => setMovieSearch(e.target.value)}
              placeholder="Търси филм..."
            />
            <select className={INP} value={movieId} onChange={(e) => setMovieId(e.target.value)}>
              <option value="" className="text-black">Всички филми</option>
              {filteredMovies.map((m) => (
                <option key={m.movie_id} value={m.movie_id} className="text-black">
                  {m.movie_name}
                </option>
              ))}
            </select>
          </label>

          <label>
            <div className={LABEL}>Дата</div>
            <input className={INP} type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>

          <div className="flex items-end gap-2">
            <button className={BTN} onClick={loadRows} disabled={loading}>
              {loading ? "Зарежда..." : "Обнови"}
            </button>
            <button className={BTN_LITE} onClick={() => { setMovieId(""); setDate(""); }}>
              Изчисти
            </button>
          </div>
        </div>

        {editing && (
          <div className={`${CARD} mb-5`}>
            <h2 className="text-xl font-bold mb-3">
              {editing.movie_name || "Филм"} — {editing.show_date} {editing.show_time}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <label>
                <div className={LABEL}>Базова цена</div>
                <input className={INP} type="number" step="0.01" value={basePrice} onChange={(e) => setBasePrice(e.target.value)} />
              </label>

              <label>
                <div className={LABEL}>Балкон +</div>
                <input className={INP} type="number" step="0.01" value={balconyExtra} onChange={(e) => setBalconyExtra(e.target.value)} />
              </label>

              <label>
                <div className={LABEL}>VIP +</div>
                <input className={INP} type="number" step="0.01" value={vipExtra} onChange={(e) => setVipExtra(e.target.value)} />
              </label>
            </div>

            <div className="flex flex-wrap gap-2 mt-4">
              <button className={BTN} onClick={save}>Запази</button>
              <button className={BTN_DANGER} onClick={() => removeOverride()}>Изтрий override</button>
              <button className={BTN_LITE} onClick={() => setEditing(null)}>Отказ</button>
            </div>
          </div>
        )}

        <div className={CARD}>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-white/70">
                <tr className="border-b border-white/10">
                  <th className="text-left py-2 pr-3">Дата</th>
                  <th className="text-left py-2 pr-3">Час</th>
                  <th className="text-left py-2 pr-3">Филм</th>
                  <th className="text-left py-2 pr-3">Зала</th>
                  <th className="text-left py-2 pr-3">Формат</th>
                  <th className="text-left py-2 pr-3">База</th>
                  <th className="text-left py-2 pr-3">Балкон +</th>
                  <th className="text-left py-2 pr-3">VIP +</th>
                  <th className="text-left py-2 pr-3">Статус</th>
                  <th className="text-left py-2 pr-3">Действие</th>
                </tr>
              </thead>

              <tbody>
                {filteredRows.map((r) => (
                  <tr key={r.SHOWTIME_ID} className="border-b border-white/5 hover:bg-white/5">
                    <td className="py-2 pr-3">{r.show_date}</td>
                    <td className="py-2 pr-3">{r.show_time}</td>
                    <td className="py-2 pr-3">{r.movie_name || "-"}</td>
                    <td className="py-2 pr-3">{r.hall_name || "-"}</td>
                    <td className="py-2 pr-3">{r.FORMAT_TYPE || "-"}</td>
                    <td className="py-2 pr-3">{r.base_price === "" ? "-" : r.base_price}</td>
                    <td className="py-2 pr-3">{r.balcony_extra === "" ? "-" : r.balcony_extra}</td>
                    <td className="py-2 pr-3">{r.vip_extra === "" ? "-" : r.vip_extra}</td>
                    <td className="py-2 pr-3">{r.has_override ? "✅ custom" : "default"}</td>
                    <td className="py-2 pr-3 flex gap-2">
                      <button className={BTN_LITE} onClick={() => editRow(r)}>Edit</button>
                      {r.has_override && (
                        <button className={BTN_DANGER} onClick={() => removeOverride(r)}>Delete</button>
                      )}
                    </td>
                  </tr>
                ))}

                {!filteredRows.length && (
                  <tr>
                    <td colSpan="10" className="py-8 text-center text-white/60">
                      Няма прожекции за избраните филтри.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
}
